import React, { useEffect, useRef } from "react";
import L from "leaflet";
import operatorApiService from "../../../../services/operator-api.service";
import { LobbyRefreshData, Vehicle } from "../../../../types/lobby";
import "leaflet/dist/leaflet.css";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

import "./Map.scss";
import pinNoKsWhite from "../../../../assets/imgs/lobby/map/map_pin_noks_white.svg";
import pinNoKsGreen from "../../../../assets/imgs/lobby/map/map_pin_noks_green.svg";
import pinNoKsYellow from "../../../../assets/imgs/lobby/map/map_pin_noks_yellow.svg";
import pinNoKsRed from "../../../../assets/imgs/lobby/map/map_pin_noks_red.svg";
import pinKsWhite from "../../../../assets/imgs/lobby/map/map_pin_ks_white.svg";
import pinKsGreen from "../../../../assets/imgs/lobby/map/map_pin_ks_green.svg";
import pinKsYellow from "../../../../assets/imgs/lobby/map/map_pin_ks_yellow.svg";
import pinKsRed from "../../../../assets/imgs/lobby/map/map_pin_ks_red.svg";

interface MapProps {
  baseId: number | null;
  baseName: string;
  vehicles: Vehicle[];
  refreshDataMap: Record<string, LobbyRefreshData>;
}

const Map: React.FC<MapProps> = ({
  baseId,
  baseName,
  vehicles,
  refreshDataMap,
}) => {
  const mapRef = useRef<L.Map | null>(null);
  const markerRefs = useRef<Record<string, L.Marker>>({});

  const getPinImage = (isMonitoring: boolean, crArray: any[]): string => {
    const priorityToColor: Record<number, string> = {
      1: "alrt-gr",
      2: "alrt-ye",
      3: "alrt-rd",
    };
    const filteredArray =
      crArray?.filter((cr) => cr.confirmation_response_cd !== "2") ?? [];
    const colorCode =
      filteredArray.length === 0
        ? ""
        : priorityToColor[
            Math.max(...(filteredArray?.map((cr) => cr.priority ?? 0) ?? [0]))
          ] ?? "";

    if (isMonitoring) {
      switch (colorCode) {
        case "alrt-gr":
          return pinKsGreen;
        case "alrt-gr2":
          return pinKsGreen;
        case "alrt-ye":
          return pinKsYellow;
        case "alrt-rd":
          return pinKsRed;
        default:
          return pinKsWhite;
      }
    } else {
      switch (colorCode) {
        case "alrt-gr":
          return pinNoKsGreen;
        case "alrt-gr2":
          return pinNoKsGreen;
        case "alrt-ye":
          return pinNoKsYellow;
        case "alrt-rd":
          return pinNoKsRed;
        default:
          return pinNoKsWhite;
      }
    }
  };

  useEffect(() => {
    if (!baseId) return;

    operatorApiService
      .doGetBasePoint({ baseId })
      .then((results) => {
        const point = results[0];
        if (!point) return;

        const map = L.map("mapid", {
          center: [point.map_latitude, point.map_longitude],
          zoom: point.map_zoom,
          closePopupOnClick: false,
        });

        mapRef.current = map;

        L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
          attribution:
            '© <a href="http://osm.org/copyright">OpenStreetMap</a> contributors',
        }).addTo(map);

        // 停留所ピン
        const courseLayer = L.layerGroup();
        operatorApiService.doGetMapPoints({ baseId }).then((points) => {
          const stopIcon = L.icon({
            iconUrl: markerIcon,
            iconRetinaUrl: markerIcon2x,
            shadowUrl: markerShadow,
            iconSize: [25, 41],
            iconAnchor: [12, 41],
            popupAnchor: [1, -34],
            shadowSize: [41, 41],
          });

          points.forEach((point: any) => {
            const lat = Number(point.map_latitude);
            const lng = Number(point.map_longitude);
            if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

            const m = L.marker([lat, lng], { icon: stopIcon });
            if (point.base_stop_name) {
              m.bindPopup(point.base_stop_name, { autoClose: false });
            }
            courseLayer.addLayer(m);
          });
          L.control
            .layers(undefined, { [`${baseName}コース`]: courseLayer })
            .addTo(map);
          courseLayer.addTo(map);
        });
      })
      .catch((err) => {
        console.error("地図初期ポイント取得エラー:", err);
      });

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
      }
    };
  }, [baseId]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    Object.entries(refreshDataMap).forEach(([vcc, data]) => {
      const gps = data?.gps;
      if (!gps) return;

      const lat = gps.latitude;
      const lng = gps.longitude;
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

      const isMonitoring = !!data?.monitor;
      const crArray = data?.cr ?? [];
      const vehicle = vehicles.find((v) => v.vehicle_id === gps.vehicle_id);
      const vehicleNo = vehicle?.vehicle_no ?? "";

      const iconHtml = `
        <div class="vcPinWrapper">
          <img src="${getPinImage(isMonitoring, crArray)}"/>
          <div class="vcPinLabel">${vehicleNo}</div>
        </div>
      `;

      const pinIcon = L.divIcon({
        html: iconHtml,
        className: "customVcPin",
        iconSize: [120, 55],
        iconAnchor: [1.5, 53.5],
      });

      if (markerRefs.current[vcc]) {
        markerRefs.current[vcc].setLatLng([lat, lng]).setIcon(pinIcon);
      } else {
        const marker = L.marker([lat, lng], { icon: pinIcon })
          .addTo(map)
          .bindPopup(data.monitor?.vehicle_label || vcc, {
            offset: [1.5, -1],
            autoClose: false,
          });
        markerRefs.current[vcc] = marker;
      }
    });
  }, [refreshDataMap]);

  return <div id="mapid" style={{ width: "100%", height: "100%" }}></div>;
};

export default Map;
